import { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_SECONDARY } from './brandDashboardTokens';
import type {
    IFundWalletPaystackResponse,
    IWalletFundOptions,
    IWalletFundPreset,
    WalletFundProvider,
} from '../../redux/buzzbridge/types';
import { fundWallet } from '../../redux/buzzbridge/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { buildCampaignWalletFundBody, storePaystackFundReference } from './walletFundRailUtils';
import {
    buildWalletFeeCalcPayload,
    chargesInPaymentCurrency,
    formatYouPayDisplay,
    getMinimumEnterAmountPrompt,
    isAdminTestPreset,
    isCustomGrossPaymentMode,
    isWalletTopUpAmountAllowed,
} from './walletFundOptionsUtils';
import { api } from '../../configurations/api';
import { calculateFeesUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import { WalletFundingSummary } from './WalletFundingSummary';
import {
    formatTotalPayLabelFromSalesCalc,
    formatWalletCreditLabel,
    resolveWalletCreditUsdCents,
    resolveWalletCreditUsdCentsFromSalesCalc,
    salesCalcPreviewIsValid,
    type ICampaignWalletSalesCalc,
    unwrapSalesCalcBody,
} from './walletSalesCalcUtils';

const FEE_PREVIEW_DEBOUNCE_MS = 350;

export interface WalletPaystackFundingProps {
    walletCreditUsdCents: number;
    fundOptions: IWalletFundOptions | null;
    /** User-clicked preset only — not auto-matched from custom amount */
    selectedPreset: IWalletFundPreset | null;
    activePreset: IWalletFundPreset | null;
    /** Gross local-currency charge cents when user typed a custom amount */
    customPaymentChargeCents?: number | null;
    onSwitchToStripe: () => void;
    stripeAvailable: boolean;
    /** Hosted checkout provider — Paystack or Flutterwave. */
    provider?: Extract<WalletFundProvider, 'paystack' | 'flutterwave'>;
}

export function WalletPaystackFunding({
    walletCreditUsdCents,
    fundOptions,
    selectedPreset,
    activePreset,
    customPaymentChargeCents = null,
    onSwitchToStripe,
    stripeAvailable,
    provider = 'paystack',
}: WalletPaystackFundingProps) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(false);
    const [feePreview, setFeePreview] = useState<ICampaignWalletSalesCalc | null>(null);
    const [feeLoading, setFeeLoading] = useState(false);

    const hasValidAmount = isWalletTopUpAmountAllowed(
        walletCreditUsdCents,
        fundOptions,
        selectedPreset,
        customPaymentChargeCents,
    );
    const chargesInLocal = chargesInPaymentCurrency(fundOptions);
    const adminPresetReady = Boolean(selectedPreset && isAdminTestPreset(selectedPreset) && activePreset);
    const isCustomGross =
        !selectedPreset &&
        customPaymentChargeCents != null &&
        customPaymentChargeCents >= 100 &&
        isCustomGrossPaymentMode(fundOptions);

    const resolvedWalletCreditCents = resolveWalletCreditUsdCents(feePreview, walletCreditUsdCents, selectedPreset);
    const walletCreditForFund = resolveWalletCreditUsdCentsFromSalesCalc(feePreview, walletCreditUsdCents);
    const walletCreditLabel = formatWalletCreditLabel(feePreview, walletCreditUsdCents, fundOptions, selectedPreset);
    const previewValid =
        feePreview != null &&
        salesCalcPreviewIsValid(feePreview, walletCreditForFund, fundOptions, customPaymentChargeCents, selectedPreset);
    const canCheckout =
        hasValidAmount &&
        (adminPresetReady || (feePreview != null && (!isCustomGross || (previewValid && walletCreditForFund > 0))));

    useEffect(() => {
        if (!hasValidAmount) {
            setFeePreview(null);
            return;
        }
        let cancelled = false;
        const timer = window.setTimeout(() => {
            setFeeLoading(true);
            setFeePreview(null);
            api(
                calculateFeesUrl,
                httpRequest.POST,
                buildWalletFeeCalcPayload(walletCreditUsdCents, fundOptions, {
                    selectedPreset,
                    paymentChargeCents: customPaymentChargeCents ?? undefined,
                    provider,
                }),
                0,
                0,
            )
                .then((res: unknown) => {
                    if (!cancelled) setFeePreview(unwrapSalesCalcBody(res));
                })
                .catch(() => {
                    if (!cancelled) setFeePreview(null);
                })
                .finally(() => {
                    if (!cancelled) setFeeLoading(false);
                });
        }, FEE_PREVIEW_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [walletCreditUsdCents, hasValidAmount, fundOptions, selectedPreset, customPaymentChargeCents, provider]);

    const handleHostedCheckout = async () => {
        if (!hasValidAmount) return;
        setLoading(true);
        const providerLabel = provider === 'flutterwave' ? 'Flutterwave' : 'Paystack';
        try {
            const res = (await dispatchPromise(
                fundWallet(
                    buildCampaignWalletFundBody(resolvedWalletCreditCents, {
                        provider,
                        preset: selectedPreset,
                        fundOptions,
                        feePreview,
                        customPaymentChargeCents,
                    }),
                ),
            )) as IFundWalletPaystackResponse;

            const ref = res?.reference ?? res?.tx_ref;
            if (!res?.authorization_url || !ref) {
                showToastMessage(`Could not start ${providerLabel} checkout. Try card payment instead.`, 'error');
                return;
            }

            storePaystackFundReference(ref, provider);
            const creditLabel = res.wallet_credit_usd_formatted ?? walletCreditLabel;
            try {
                sessionStorage.setItem('campaign_wallet_fund_credit_label', creditLabel);
            } catch {
                /* ignore */
            }
            window.location.assign(res.authorization_url);
        } catch (err: unknown) {
            const message =
                (err as { data?: { message?: string } })?.data?.message ??
                (err as Error)?.message ??
                `${providerLabel} checkout could not be started.`;
            if (/(paystack|flutterwave|not available|unsupported)/i.test(message) && stripeAvailable) {
                showToastMessage(
                    `${providerLabel} is not available for your region. Use card payment (Stripe).`,
                    'error',
                );
                onSwitchToStripe();
            } else {
                showToastMessage(message, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const payLine =
        formatTotalPayLabelFromSalesCalc(feePreview, fundOptions, activePreset) ??
        (activePreset ? formatYouPayDisplay(activePreset, fundOptions) : null);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1.5, lineHeight: 1.5 }}>
                Pay with bank transfer, card, USSD, or mobile money via{' '}
                {provider === 'flutterwave' ? 'Flutterwave' : 'Paystack'}. Your wallet is credited in USD after payment
                clears.
            </Typography>

            {hasValidAmount && feeLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                    <CircularProgress size={18} sx={{ color: t.primary_orange }} />
                    <Typography sx={{ fontSize: 13, color: t.form_label }}>Calculating fees…</Typography>
                </Box>
            )}

            {hasValidAmount && feePreview && !feeLoading && (
                <WalletFundingSummary
                    fee={feePreview}
                    fallbackWalletCreditUsdCents={walletCreditUsdCents}
                    fundOptions={fundOptions}
                    selectedPreset={selectedPreset}
                    activePreset={activePreset}
                    chargesInLocal={chargesInLocal}
                    themeStyle={themeStyle}
                />
            )}

            <Button
                fullWidth
                variant="contained"
                disabled={!canCheckout || loading || feeLoading}
                onClick={handleHostedCheckout}
                startIcon={
                    loading ? (
                        <CircularProgress size={20} sx={{ color: '#392700' }} />
                    ) : (
                        <AccountBalanceWalletOutlinedIcon />
                    )
                }
                sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 15,
                    bgcolor: BRAND_SECONDARY,
                    color: '#392700',
                    '&:hover': { bgcolor: BRAND_SECONDARY, filter: 'brightness(1.05)' },
                    '&.Mui-disabled': { opacity: 0.5, color: '#392700', bgcolor: BRAND_SECONDARY },
                }}>
                {loading
                    ? `Opening ${provider === 'flutterwave' ? 'Flutterwave' : 'Paystack'}…`
                    : payLine
                    ? `Continue with ${provider === 'flutterwave' ? 'Flutterwave' : 'Paystack'} · ${payLine}`
                    : `Continue with ${provider === 'flutterwave' ? 'Flutterwave' : 'Paystack'}`}
            </Button>
            {selectedPreset?.labels?.wallet_credit_secondary ? (
                <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1, textAlign: 'center' }}>
                    You will receive {selectedPreset.labels.wallet_credit_secondary}
                </Typography>
            ) : hasValidAmount ? (
                <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1, textAlign: 'center' }}>
                    You will receive {walletCreditLabel} in your wallet
                </Typography>
            ) : (
                <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1, textAlign: 'center' }}>
                    {getMinimumEnterAmountPrompt(fundOptions)}
                </Typography>
            )}
            {stripeAvailable ? (
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                    <Link
                        component="button"
                        type="button"
                        onClick={onSwitchToStripe}
                        sx={{
                            fontSize: 13,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: t.primary_orange,
                            cursor: 'pointer',
                        }}>
                        Pay with card (Stripe) instead
                    </Link>
                </Box>
            ) : null}
        </Box>
    );
}
